/**
 * Owner: Masa Bayu
 * File: demo-advanced-store.ts
 * Date:Time: 2026-09-16 18:53
 * Description: Demo Offline · Sync · Media · Flip · Motion · ADAM for T8-E2–E7 UI.
 */

import {
  createFlipIntent,
  createMediaChromeIntent,
  createQxreadEntitlement,
  enqueueAdvancedSyncItem,
  evaluateAdamEntryContext,
  markAdvancedSyncConflict,
  prepareOfflinePackage,
  purgeOfflinePackageOnRevoke,
  resolveAdvancedSyncConflict,
  upsertMotionPreference,
  type QxreadAdamEntryContext,
  type QxreadAdvancedSyncItem,
  type QxreadEntitlement,
  type QxreadFlipIntent,
  type QxreadMediaChromeIntent,
  type QxreadMotionPreference,
  type QxreadOfflinePackage,
} from '@qx/qxread-core';

const DEMO_ACCOUNT_ID = 'qxrdacc_demo_adv';
const DEMO_PUBLICATION_ID = 'qxrdpub_demo_adv';
const DEMO_NOW = '2026-09-16T10:53:00.000Z';

let demoEntitlement: QxreadEntitlement = createQxreadEntitlement({
  entitlementId: 'qxrdent_demo_adv',
  accountId: DEMO_ACCOUNT_ID,
  publicationId: DEMO_PUBLICATION_ID,
  scope: 'owned',
  state: 'active',
  createdAt: DEMO_NOW,
});
let demoPackage: QxreadOfflinePackage | null = null;
let demoSync: QxreadAdvancedSyncItem | null = null;
let demoMedia: QxreadMediaChromeIntent | null = null;
let demoFlip: QxreadFlipIntent | null = null;
let demoMotion: QxreadMotionPreference | null = null;
let demoAdam: QxreadAdamEntryContext | null = null;
let lastStatus = 'Advanced demo idle.';

function refreshEntitlement(state: QxreadEntitlement['state']): void {
  demoEntitlement = createQxreadEntitlement({
    entitlementId: 'qxrdent_demo_adv',
    accountId: DEMO_ACCOUNT_ID,
    publicationId: DEMO_PUBLICATION_ID,
    scope: 'owned',
    state,
    createdAt: DEMO_NOW,
  });
}

export function resetDemoAdvancedStore(): void {
  refreshEntitlement('active');
  demoPackage = null;
  demoSync = null;
  demoMedia = null;
  demoFlip = null;
  demoMotion = null;
  demoAdam = null;
  lastStatus = 'Advanced demo idle.';
}

export function getDemoAdvancedStatus(): string {
  return lastStatus;
}

export function getDemoOfflinePackage(): QxreadOfflinePackage | null {
  return demoPackage;
}

export function getDemoAdvancedSync(): QxreadAdvancedSyncItem | null {
  return demoSync;
}

export function getDemoMediaIntent(): QxreadMediaChromeIntent | null {
  return demoMedia;
}

export function getDemoFlipIntent(): QxreadFlipIntent | null {
  return demoFlip;
}

export function getDemoMotionPreference(): QxreadMotionPreference | null {
  return demoMotion;
}

export function getDemoAdamEntry(): QxreadAdamEntryContext | null {
  return demoAdam;
}

export function prepareDemoOfflinePackage(): string {
  refreshEntitlement('active');
  const result = prepareOfflinePackage({
    accountId: DEMO_ACCOUNT_ID,
    publicationId: DEMO_PUBLICATION_ID,
    entitlement: demoEntitlement,
    offlinePackageId: 'qxrdpkg_demo',
    preparedAt: DEMO_NOW,
  });
  if (!result.allowed) {
    lastStatus = result.reason;
    return lastStatus;
  }
  demoPackage = result.offlinePackage;
  lastStatus = `OfflinePackage ${demoPackage.state} · grantsEntitlement=${String(demoPackage.grantsEntitlement)} · executesFlip=${String(demoPackage.executesFlip)}`;
  return lastStatus;
}

export function denyDemoOfflinePackage(): string {
  refreshEntitlement('revoked');
  const result = prepareOfflinePackage({
    accountId: DEMO_ACCOUNT_ID,
    publicationId: DEMO_PUBLICATION_ID,
    entitlement: demoEntitlement,
    preparedAt: DEMO_NOW,
  });
  lastStatus = result.allowed ? 'Unexpected prepare allowed' : `Prepare denied · ${result.reason}`;
  return lastStatus;
}

export function purgeDemoOfflinePackage(): string {
  if (demoPackage === null) {
    lastStatus = 'Prepare an OfflinePackage first.';
    return lastStatus;
  }
  refreshEntitlement('revoked');
  demoPackage = purgeOfflinePackageOnRevoke({
    offlinePackage: demoPackage,
    entitlement: demoEntitlement,
    purgedAt: DEMO_NOW,
  });
  lastStatus = `OfflinePackage ${demoPackage.state} · grantsEntitlement=${String(demoPackage.grantsEntitlement)}`;
  return lastStatus;
}

export function enqueueDemoSync(): string {
  demoSync = enqueueAdvancedSyncItem({
    accountId: DEMO_ACCOUNT_ID,
    publicationId: DEMO_PUBLICATION_ID,
    syncItemId: 'qxrdsync_demo',
    queuedAt: DEMO_NOW,
  });
  lastStatus = `Sync ${demoSync.state} · conflictSilent=${String(demoSync.conflictSilent)}`;
  return lastStatus;
}

export function conflictDemoSync(): string {
  if (demoSync === null) {
    lastStatus = 'Enqueue a sync item first.';
    return lastStatus;
  }
  demoSync = markAdvancedSyncConflict({
    item: demoSync,
    conflictNote: 'Local progress diverged from server',
    conflictedAt: DEMO_NOW,
  });
  lastStatus = `Sync ${demoSync.state} · note=${demoSync.conflictNote ?? ''}`;
  return lastStatus;
}

export function resolveDemoSync(): string {
  if (demoSync === null || demoSync.state !== 'conflict') {
    lastStatus = 'Mark a sync conflict first.';
    return lastStatus;
  }
  demoSync = resolveAdvancedSyncConflict({ item: demoSync, resolvedAt: DEMO_NOW });
  lastStatus = `Sync ${demoSync.state} · inventsEntitlement=${String(demoSync.inventsEntitlement)}`;
  return lastStatus;
}

export function createDemoMediaIntent(): string {
  demoMedia = createMediaChromeIntent({
    publicationId: DEMO_PUBLICATION_ID,
    kind: 'caption',
    mediaIntentId: 'qxrdmed_demo',
    createdAt: DEMO_NOW,
  });
  lastStatus = `Media ${demoMedia.kind} · executesInQxread=${String(demoMedia.executesInQxread)} · executesInQxReader=${String(demoMedia.executesInQxReader)}`;
  return lastStatus;
}

export function createDemoFlipIntent(): string {
  demoFlip = createFlipIntent({
    publicationId: DEMO_PUBLICATION_ID,
    kind: 'next',
    flipIntentId: 'qxrdflip_demo',
    createdAt: DEMO_NOW,
  });
  lastStatus = `Flip ${demoFlip.kind} · executesInQxread=${String(demoFlip.executesInQxread)} · executesInQxReader=${String(demoFlip.executesInQxReader)}`;
  return lastStatus;
}

export function enableDemoReducedMotion(): string {
  demoMotion = upsertMotionPreference({
    accountId: DEMO_ACCOUNT_ID,
    reducedMotion: true,
    motionPreferenceId: 'qxrdmot_demo',
    updatedAt: DEMO_NOW,
  });
  lastStatus = `Motion reduced · flipFallback=${demoMotion.flipFallback} · executesFlip=${String(demoMotion.executesFlip)}`;
  return lastStatus;
}

export function evaluateDemoAdamAvailable(): string {
  refreshEntitlement('active');
  demoAdam = evaluateAdamEntryContext({
    accountId: DEMO_ACCOUNT_ID,
    publicationId: DEMO_PUBLICATION_ID,
    entitlement: demoEntitlement,
    readerSessionRef: 'qxrdbrg_demo',
    adamEntryId: 'qxrdadam_demo',
    createdAt: DEMO_NOW,
  });
  lastStatus = `ADAM gate=${demoAdam.gate} · ownsAdamIntelligence=${String(demoAdam.ownsAdamIntelligence)} · inventsEntitlement=${String(demoAdam.inventsEntitlement)}`;
  return lastStatus;
}

export function evaluateDemoAdamBlocked(): string {
  refreshEntitlement('expired');
  demoAdam = evaluateAdamEntryContext({
    accountId: DEMO_ACCOUNT_ID,
    publicationId: DEMO_PUBLICATION_ID,
    entitlement: demoEntitlement,
    readerSessionRef: 'qxrdbrg_demo',
    createdAt: DEMO_NOW,
  });
  lastStatus = `ADAM gate=${demoAdam.gate} · ${demoAdam.reason}`;
  return lastStatus;
}
